import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SearchAndFilter } from "../../components/shared";
import { LoadingState } from "@/components/ui/loading-state";
import { PageHeader } from "@/components/ui/page-header";
import {
  RotateCcw,
  Sparkles,
  Phone,
  TrendingUp,
  Plus,
  Eye,
  Edit,
  Play,
} from "lucide-react";
import { Button, Card } from "../../components/ui";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@/components/ui/table";
import { PageIntent } from "../../docs/ux/page-intents";

// Page Intent: CONFIGURATION - table-first, no stats cards, no charts
const pageIntent: PageIntent = "CONFIGURATION";

interface CallRouting {
  id: string;
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE" | "DRAFT";
  type: "SKILL_BASED" | "PRIORITY_BASED" | "TIME_BASED" | "CUSTOMER_BASED";
  priority: number;
  conditions: string[];
  actions: string[];
  targetQueue: string;
  targetAgent?: string;
  fallbackQueue: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  metrics: {
    totalCalls: number;
    routedCalls: number;
    avgRoutingTime: number;
    successRate: number;
    lastUsed?: string;
  };
}

const CallRoutingPage: React.FC = () => {
  const { t } = useTranslation();
  const [routingRules, setRoutingRules] = useState<CallRouting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Mock data - replace with actual API calls
  useEffect(() => {
    const mockRoutingRules: CallRouting[] = [
      {
        id: "1",
        name: "VIP Customer Routing",
        description: "Route VIP customers to senior agents",
        status: "ACTIVE",
        type: "CUSTOMER_BASED",
        priority: 1,
        conditions: ["Customer tier = VIP", "Language = English"],
        actions: ["Route to Senior Queue", "Set priority = High"],
        targetQueue: "Senior Support",
        fallbackQueue: "General Support",
        createdBy: "John Admin",
        createdAt: "2024-01-10T08:00:00Z",
        updatedAt: "2024-01-15T10:00:00Z",
        tags: ["vip", "priority"],
        metrics: {
          totalCalls: 450,
          routedCalls: 445,
          avgRoutingTime: 2.3,
          successRate: 98.9,
          lastUsed: "2024-01-15T10:30:00Z",
        },
      },
      {
        id: "2",
        name: "Technical Support Routing",
        description: "Route technical issues to specialized agents",
        status: "ACTIVE",
        type: "SKILL_BASED",
        priority: 2,
        conditions: ["Issue type = Technical", "Complexity = High"],
        actions: ["Route to Technical Queue", "Assign to skilled agent"],
        targetQueue: "Technical Support",
        fallbackQueue: "General Support",
        createdBy: "Sarah Manager",
        createdAt: "2024-01-08T09:00:00Z",
        updatedAt: "2024-01-14T14:00:00Z",
        tags: ["technical", "skills"],
        metrics: {
          totalCalls: 890,
          routedCalls: 875,
          avgRoutingTime: 3.1,
          successRate: 98.3,
          lastUsed: "2024-01-14T15:45:00Z",
        },
      },
      {
        id: "3",
        name: "Business Hours Routing",
        description: "Route calls based on business hours",
        status: "ACTIVE",
        type: "TIME_BASED",
        priority: 3,
        conditions: ["Time = Business Hours", "Day = Weekday"],
        actions: ["Route to Available Agents", "Set SLA = 5 minutes"],
        targetQueue: "General Support",
        fallbackQueue: "After Hours",
        createdBy: "Mike Support",
        createdAt: "2024-01-05T11:00:00Z",
        updatedAt: "2024-01-15T09:00:00Z",
        tags: ["time-based", "business-hours"],
        metrics: {
          totalCalls: 1250,
          routedCalls: 1230,
          avgRoutingTime: 1.8,
          successRate: 98.4,
          lastUsed: "2024-01-15T09:15:00Z",
        },
      },
      {
        id: "4",
        name: "Billing Priority Routing",
        description: "Route billing issues with high priority",
        status: "ACTIVE",
        type: "PRIORITY_BASED",
        priority: 1,
        conditions: ["Issue type = Billing", "Urgency = High"],
        actions: ["Route to Billing Queue", "Set priority = Critical"],
        targetQueue: "Billing Support",
        fallbackQueue: "General Support",
        createdBy: "Lisa Admin",
        createdAt: "2024-01-03T10:00:00Z",
        updatedAt: "2024-01-15T08:00:00Z",
        tags: ["billing", "priority"],
        metrics: {
          totalCalls: 320,
          routedCalls: 315,
          avgRoutingTime: 2.5,
          successRate: 98.4,
          lastUsed: "2024-01-15T08:30:00Z",
        },
      },
      {
        id: "5",
        name: "Language-Based Routing",
        description: "Route calls based on customer language preference",
        status: "DRAFT",
        type: "CUSTOMER_BASED",
        priority: 4,
        conditions: ["Language = Arabic", "Region = Middle East"],
        actions: ["Route to Arabic Queue", "Assign Arabic-speaking agent"],
        targetQueue: "Arabic Support",
        fallbackQueue: "General Support",
        createdBy: "Alex Support",
        createdAt: "2024-01-12T14:00:00Z",
        updatedAt: "2024-01-15T16:00:00Z",
        tags: ["language", "arabic"],
        metrics: {
          totalCalls: 0,
          routedCalls: 0,
          avgRoutingTime: 0,
          successRate: 0,
        },
      },
    ];

    setRoutingRules(mockRoutingRules);
    setLoading(false);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "gray";
      case "DRAFT":
        return "warning";
      default:
        return "gray";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "SKILL_BASED":
        return "info";
      case "PRIORITY_BASED":
        return "warning";
      case "TIME_BASED":
        return "primary";
      case "CUSTOMER_BASED":
        return "success";
      default:
        return "gray";
    }
  };

  const filteredRoutingRules = routingRules.filter(rule => {
    const matchesStatus =
      selectedStatus === "all" || rule.status === selectedStatus;
    const matchesType = selectedType === "all" || rule.type === selectedType;
    const matchesSearch =
      rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.tags.some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );

    return matchesStatus && matchesType && matchesSearch;
  });

  if (loading) {
    return <LoadingState />;
  }

  const filterOptions = [
    {
      label: t("all-statuses"),
      value: "all",
    },
    {
      label: t("active"),
      value: "ACTIVE",
    },
    {
      label: t("inactive"),
      value: "INACTIVE",
    },
    {
      label: t("draft"),
      value: "DRAFT",
    },
  ];

  const typeOptions = [
    {
      label: t("all-types"),
      value: "all",
    },
    {
      label: t("skill-based"),
      value: "SKILL_BASED",
    },
    {
      label: t("priority-based"),
      value: "PRIORITY_BASED",
    },
    {
      label: t("time-based"),
      value: "TIME_BASED",
    },
    {
      label: t("customer-based"),
      value: "CUSTOMER_BASED",
    },
  ];

  return (
    <div className="min-h-full bg-white">
      <PageHeader
        title={t("callRouting")}
        description={t("call-routing-description")}
        icon={RotateCcw}
        iconBgColor="from-blue-500 to-blue-600"
        actions={
          <Button variant="primary" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            {t("create-rule")}
          </Button>
        }
      />

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t("search-for-rule")}
          filters={[
            {
              label: t("status"),
              value: selectedStatus,
              options: filterOptions,
              onChange: setSelectedStatus,
            },
            {
              label: t("type"),
              value: selectedType,
              options: typeOptions,
              onChange: setSelectedType,
            },
          ]}
          resultsCount={filteredRoutingRules.length}
          totalCount={routingRules.length}
          resultsLabel={t("rules")}
        />

        <Card className="mb-6">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("name")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("type")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("priority")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("status")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("target-queue")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("total-calls")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("success-rate")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("avg-routing-time")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("created-by")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRoutingRules.map(rule => (
                <TableRow key={rule.id}>
                  <TableCell className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div>
                      <div className="font-medium">{rule.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {rule.description}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getTypeColor(rule.type) === "info"
                          ? "bg-blue-100 text-blue-800"
                          : getTypeColor(rule.type) === "warning"
                            ? "bg-yellow-100 text-yellow-800"
                            : getTypeColor(rule.type) === "primary"
                              ? "bg-primary-100 text-primary-800"
                              : getTypeColor(rule.type) === "success"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(rule.type.toLowerCase().replace("_", "-"))}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {rule.priority}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getStatusColor(rule.status) === "success"
                          ? "bg-green-100 text-green-800"
                          : getStatusColor(rule.status) === "warning"
                            ? "bg-yellow-100 text-yellow-800"
                            : getStatusColor(rule.status) === "gray"
                              ? "bg-gray-100 text-gray-800"
                              : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(rule.status.toLowerCase())}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900">
                    {rule.targetQueue}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {rule.metrics.totalCalls.toLocaleString()}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {rule.metrics.successRate}%
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {rule.metrics.avgRoutingTime}s
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {rule.createdBy}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Play className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {filteredRoutingRules.length === 0 && (
          <Card className="text-center py-12">
            <RotateCcw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("no-rules-found")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("no-rules-description")}
            </p>
            <Button variant="primary" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              {t("create-rule")}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CallRoutingPage;
